import React, { memo, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Typography, useTheme } from '@mui/material';
import { SuccessIcon } from '@sharedCR/coreDesignSystem/icons';
import { emailNotificationSkynetApi } from 'app/services/api';
import Skeleton from 'app/shared/components/common/Skeleton';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { COMMON_DATETIME_FORMAT } from 'constants/date';
import { SKELETON_TYPES } from 'constants/skeleton';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getScrollStyles } from 'styles/scrollbar.styles';

import { closeDialogAction } from 'store/global/actions';

import { ActivitiesTestIds } from '../../../Activities.testIds';

const EmailStatusPopup = ({ applicationId, emailId, events: eventsFromActivity }) => {
  const theme = useTheme();
  const classes = getScrollStyles(theme);
  const dispatch = useDispatch();

  const onCloseClick = useCallback(() => dispatch(closeDialogAction()), [dispatch]);

  const [events, setEvents] = useState(eventsFromActivity);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);

  useEffectSafeStrictMode(() => {
    setIsLoadingEvents(true);
    emailNotificationSkynetApi
      .getEmailActions({ applicationId, emailId })
      .then(({ data }) => {
        setEvents(
          eventsFromActivity.concat(
            data.map(({ actionDateTime, ...item }) => ({
              ...item,
              eventDateTime: actionDateTime,
            })),
          ),
        );
      })
      .catch(Error)
      .finally(() => setIsLoadingEvents(false));
  }, [applicationId, emailId, eventsFromActivity]);

  return (
    <div className="flex flex-col p-24 w-400">
      <Box className="max-h-200 overflow-y-auto" sx={classes.scroll}>
        {isLoadingEvents && <Skeleton length={3} type={SKELETON_TYPES.LIST} />}
        {!isLoadingEvents &&
          _.orderBy(events, 'eventDateTime', 'desc').map(({ eventDateTime, type }, index) => (
            <div
              className="flex items-center justify-between my-12"
              key={`${eventDateTime}-${index}`}
            >
              <Typography className="flex items-center mr-32" variant="regular14">
                <SuccessIcon className="text-green mr-2" />
                {type}
              </Typography>
              <Typography variant="regular14">
                {dayjs(eventDateTime).format(COMMON_DATETIME_FORMAT)}
              </Typography>
            </div>
          ))}
      </Box>
      <Button
        className="self-end mt-24"
        data-testid={ActivitiesTestIds.EMAIL_STATUS_CLOSE_BUTTON}
        type="button"
        variant="contained"
        onClick={onCloseClick}
      >
        Close
      </Button>
    </div>
  );
};

EmailStatusPopup.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({ type: PropTypes.string, eventDateTime: PropTypes.string }),
  ).isRequired,
  applicationId: PropTypes.string.isRequired,
  emailId: PropTypes.string.isRequired,
};

export default memo(EmailStatusPopup);
